// Pick the schedule activity - or a whole category of it - that a checklist
// item belongs to (owner, 2026-09-18: "make checklists able to show on all
// 3, management, field and issues (BIM) on the schedule, and we just select
// which activity or category of the schedule during creation").
//
// The schedule's own tree, under its three lanes: BIM coordination (issues),
// Field (install), Management. A leaf is an activity; a container (a level,
// an area, a package) is a category - picking it files the item under that
// row, and the schedule draws it there, in that lane.
//
//   <ActivityPicker items={feed.items} value={id} onChange={(id) => ...} />
import React, { useMemo } from "react";
import { TreeSelect } from "antd";
import { activityLabel, activityTitle } from "helpers/schedule/activityLabel";

export const LANES = [
  { key: "bim", label: "BIM coordination (issues)" },
  { key: "field", label: "Field (install)" },
  { key: "management", label: "Management" },
  { key: "", label: "Other schedule rows" },
];

const laneOf = (it, byId) => {
  let cur = it;
  for (let i = 0; cur && i < 12; i += 1) {
    if (cur.phase) return cur.phase === "bim" || cur.phase === "field" || cur.phase === "management" ? cur.phase : "";
    cur = cur.parentId ? byId.get(String(cur.parentId)) : null;
  }
  return "";
};

export function buildActivityTree(items) {
  const list = (items || []).filter((it) => it && it._id && !it.deleted);
  const byId = new Map(list.map((it) => [String(it._id), it]));
  const kids = new Map();
  list.forEach((it) => {
    const p = it.parentId && byId.has(String(it.parentId)) ? String(it.parentId) : "";
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(it);
  });
  const order = (a, b) => (Number(a.sortIndex) || 0) - (Number(b.sortIndex) || 0) || String(a.name).localeCompare(String(b.name));
  // `title` stays the bare name: inside an OPEN tree the indentation is
  // already the path, and repeating it on every node would be noise. What
  // needed fixing is the CLOSED box, which shows one line with no tree
  // around it - `treeNodeLabelProp="label"` is what antd renders there, so
  // that is where the path goes (owner: "seeing level3 3 times").
  const node = (it, ancestors = []) => {
    const children = (kids.get(String(it._id)) || []).sort(order).map((c) => node(c, [...ancestors, String(it.name || "").trim()]));
    const end = it.endDate ? ` · ends ${String(it.endDate).slice(0, 10)}` : "";
    const withPath = { name: it.name, path: ancestors.filter(Boolean) };
    return {
      value: String(it._id),
      title: `${it.name}${children.length ? " (category)" : end}`,
      label: activityLabel(withPath),
      fullPath: activityTitle(withPath),
      // Search has to reach the path too, or typing a level name finds
      // nothing - the tree filters on this prop, not on what it renders.
      searchText: `${activityTitle(withPath)} ${it.name || ""}`,
      children: children.length ? children : undefined,
    };
  };
  const roots = (kids.get("") || []).sort(order);
  return LANES.map((lane) => {
    const members = roots.filter((r) => laneOf(r, byId) === lane.key);
    return members.length ? { value: `lane:${lane.key || "other"}`, title: lane.label, label: lane.label, searchText: lane.label, selectable: false, children: members.map((m) => node(m, [])) } : null;
  }).filter(Boolean);
}

export default function ActivityPicker({ items, value, onChange, placeholder = "Schedule activity or category", style, size, status }) {
  const tree = useMemo(() => buildActivityTree(items), [items]);
  return (
    <TreeSelect
      showSearch allowClear size={size} status={status}
      style={{ minWidth: 300, ...(style || {}) }}
      dropdownStyle={{ maxHeight: 420, overflow: "auto" }}
      treeNodeFilterProp="searchText"
      treeNodeLabelProp="label"
      placeholder={placeholder}
      value={value || undefined}
      onChange={(v) => onChange && onChange(v && !String(v).startsWith("lane:") ? v : "")}
      treeData={tree}
    />
  );
}
